import React, { useState } from 'react'
import { X, Monitor, Tv, Check } from 'lucide-react'

const AttachChannelModal = ({ channel, devices, onAttach, onClose }) => {
  const [selectedDeviceId, setSelectedDeviceId] = useState('')
  const [isAttaching, setIsAttaching] = useState(false)

  // Helper function to get channel name by ID
  const getChannelName = (channelId) => {
    // This would need to be passed from parent component or fetched
    // For now, we'll show a generic message
    return 'existing channel'
  }

  const handleAttach = async () => {
    if (!selectedDeviceId) {
      return
    }

    setIsAttaching(true)
    try {
      await onAttach(selectedDeviceId)
    } finally {
      setIsAttaching(false)
    }
  }

  const selectedDevice = devices.find(device => device.key === selectedDeviceId)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <Tv className="w-6 h-6 text-primary-600 mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">Attach Channel</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Channel Info */}
          <div className="mb-6">
            <h3 className="font-medium text-gray-900 mb-2">Channel Details</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                {channel.thumbnail ? (
                  <img
                    src={channel.thumbnail}
                    alt={channel.name}
                    className="w-16 h-12 object-cover rounded"
                  />
                ) : (
                  <div className="w-16 h-12 bg-gray-200 rounded flex items-center justify-center">
                    <Tv className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{channel.name}</h4>
                  <p className="text-sm text-gray-600">#{channel.number}</p>
                  {channel.description && (
                    <p className="text-sm text-gray-600 mt-1">{channel.description}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Device Selection */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Device
            </label>
            
            {devices.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                <Monitor className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900 mb-1">No devices found</h3>
                <p className="text-gray-600 mb-4">You need to add a device first to attach channels.</p>
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Go to Devices
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {devices.map((device) => (
                  <div
                    key={device.key}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedDeviceId === device.key
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedDeviceId(device.key)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-lg ${
                          selectedDeviceId === device.key
                            ? 'bg-primary-100 text-primary-600'
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          <Monitor className="w-5 h-5" />
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{device.name}</h4>
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-gray-600">{device.manufacturer && device.model ? `${device.manufacturer} ${device.model}` : 'Unknown model'}</p>
                            {device.limited && (
                              <div className="flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                                <Tv className="w-3 h-3" />
                                Channel
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {selectedDeviceId === device.key && (
                        <Check className="w-5 h-5 text-primary-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Selected Device Info */}
          {selectedDevice && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium text-blue-900 mb-2">Selected Device</h4>
              <div className="flex items-center space-x-3">
                <Monitor className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">{selectedDevice.name}</p>
                  <p className="text-sm text-blue-700">{selectedDevice.manufacturer && selectedDevice.model ? `${selectedDevice.manufacturer} ${selectedDevice.model}` : 'Unknown model'}</p>
                </div>
              </div>
              
              {/* Warning if device already has a channel */}
              {selectedDevice.channelId && selectedDevice.channelId !== '' && (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                      <span className="text-white text-xs">!</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Channel Replacement</p>
                      <p className="text-xs text-yellow-700">
                        This device already has a channel assigned. Attaching "{channel.name}" will replace the existing channel.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            disabled={isAttaching}
          >
            Cancel
          </button>
          <button
            onClick={handleAttach}
            disabled={!selectedDeviceId || isAttaching}
            className={`px-4 py-2 rounded-lg transition-colors ${
              selectedDeviceId && !isAttaching
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {isAttaching ? 'Attaching...' : 
              (selectedDevice?.channelId && selectedDevice.channelId !== '' ? 'Replace Channel' : 'Attach Channel')
            }
          </button>
        </div>
      </div>
    </div>
  )
}

export default AttachChannelModal 